import { toastManager } from '@agently/ui';
import { camelizeKeys } from '@agently/utils';
import axios, { AxiosError } from 'axios';
import { decamelizeKeys } from 'fast-case';
import qs from 'query-string';

import { getAuthToken, handleUnauthorized } from './auth-token';

const BASE_URL = import.meta.env.VITE_BASE_URL as string;

const defaultTransformers = <T>(transformRequest?: T | T[]): T[] => {
  if (!transformRequest) {
    return [];
  }
  if (transformRequest instanceof Array) {
    return transformRequest;
  }

  return [transformRequest];
};

export const api = axios.create({
  baseURL: BASE_URL,
  paramsSerializer: {
    serialize: (params) =>
      qs.stringify(decamelizeKeys(params), {
        skipEmptyString: true,
        skipNull: true,
        arrayFormat: 'comma',
      }),
  },
  transformRequest: [
    (data: any): unknown => {
      if (data instanceof FormData) {
        return data;
      }
      return decamelizeKeys(data);
    },
    ...defaultTransformers(axios.defaults.transformRequest),
  ],
  transformResponse: [
    ...defaultTransformers(axios.defaults.transformResponse),
    (data: any): unknown => camelizeKeys(data),
  ],
});
function errorHandler(error: AxiosError) {
  if (error.code === 'ECONNABORTED') {
    toastManager.add({ title: 'Connection error', type: 'error' });

    return Promise.reject(error);
  }
  if (!error.response) {
    return Promise.reject(error);
  }
  const {
    response: { status },
  } = error;
  const requestUrl = error.config?.url ?? '';
  // Tracking endpoint may return 401 for anonymous events and should not force logout.
  const isTrackRequest = requestUrl.includes('/app/track');

  if (status === 401 && !isTrackRequest) {
    handleUnauthorized();
    return Promise.reject(error);
  }

  if (status === 400) {
    const errors: any =
      (error as AxiosError)?.response?.data ?? 'Something went wrong';
    const message = errors?.errors?.[0]?.message || errors?.message;

    toastManager.add({ title: message, type: 'error' });
  }
  return Promise.reject(error);
}

api.interceptors.response.use(
  (response) => response,
  (error) => errorHandler(error),
);

// Automatically attach auth token to every request from localStorage
// This ensures token is always current and solves race conditions during cache restore
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
